import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import logoutButton from '../../../assets/images/buttons/logout-button.svg'
import { useModal } from '../../../hooks'
import { EModalName } from '../../../types/enums'
import { trpc } from '../../../utils'
import { Button, CardButton } from '../../atoms'
import { GameSettings, TableSettings } from '../../molecules'
import { TableMenuPlayers } from '..'
import type { PrivateTableSettingsType } from '../CreatePrivateTable/types'
import { TableMenuAdminSettings } from '../TableMenuAdminSettings'
import {
  adminButtonsRow,
  basement,
  content,
  drawerContainer,
  h2,
  leaveButton,
  settingsContainer,
  settingsWrapper,
} from './index.css'

export const TableMenuDrawer = (props: {
  tableId: string
  playersCount: number
  isPaused: boolean
  isAwaitingDeletion: boolean
  showAdminSettings: boolean
  close: (value: 'leave' | 'delete') => void
}) => {
  const { mutateAsync: updatePrivateTable } =
    trpc.table.updatePrivateTable.useMutation()
  const { mutateAsync: updateTableState, isPending: isUpdatingTableState } =
    trpc.table.updateTableState.useMutation()
  const { openModal, openInfoModal, openConfirmationModal, closeModal } =
    useModal()
  const { t } = useTranslation()
  const [isPaused, setIsPaused] = useState(props.isPaused)
  const [isAwaitingDeletion, setIsAwaitingDeletion] = useState(
    props.isAwaitingDeletion,
  )

  const showUpdatedSettingsModal = () =>
    openInfoModal(
      {
        text: t('modal.info.tableSettingsChanged.text'),
        buttonText: t('modal.info.tableSettingsChanged.confirm'),
      },
      {
        title: t('modal.info.tableSettingsChanged.title'),
        closeOnClickOutside: true,
        name: EModalName.INFO_TABLE_SETTINGS_CHANGED,
      },
    )

  const onClickAdminSettings = async () => {
    const { closeModal } = openModal<PrivateTableSettingsType | null>(
      ({ close }) => (
        <TableMenuAdminSettings
          tableId={props.tableId}
          playersCount={props.playersCount}
          close={close}
        />
      ),
      {
        title: t('modal.tableMenu.adminSettings.tableSettings'),
        position: 'left',
        closeOnClickOutside: true,
        name: EModalName.TABLE_ADMIN_SETTINGS,
      },
    )
    const result = await closeModal
    if (!result) {
      return
    }
    const response = await updatePrivateTable({
      ...result,
      tableId: props.tableId,
    })
    if (response) {
      showUpdatedSettingsModal()
    }
  }

  const onClickTableSettings = () => {
    openModal<PrivateTableSettingsType | null>(() => <TableSettings />, {
      title: t('settings.label.tableSettings'),
      position: 'left',
      closeOnClickOutside: true,
      name: EModalName.TABLE_SETTINGS,
    })
  }

  const onClickPlayers = () => {
    openModal(
      () => (
        <TableMenuPlayers
          tableId={props.tableId}
          showAdminSettings={props.showAdminSettings}
        />
      ),
      {
        title: t('modal.tableMenu.adminSettings.players'),
        position: 'left',
        closeOnClickOutside: true,
        name: EModalName.TABLE_PLAYERS,
      },
    )
  }

  const onClickPauseGame = async () => {
    const response = await updateTableState({
      tableId: props.tableId,
      pause: !isPaused,
    })
    if (response.success) {
      setIsPaused((value) => !value)
      showUpdatedSettingsModal()
      return
    }
  }

  const onClickDeleteTable = async () => {
    if (!isAwaitingDeletion) {
      const { closeModal } = openConfirmationModal(
        {
          text: t('modal.confirmation.deleteTable.text'),
          mainButtonText: t('modal.confirmation.deleteTable.confirm'),
          secondaryButtonText: t('modal.confirmation.deleteTable.cancel'),
        },
        {
          title: t('modal.confirmation.deleteTable.title'),
          name: EModalName.CONFIRM_TABLE_DELETE,
        },
      )
      const result = await closeModal
      if (result !== 'main') {
        return
      }
    }
    try {
      const response = await updateTableState({
        tableId: props.tableId,
        delete: !isAwaitingDeletion,
      })
      if (response.success) {
        setIsAwaitingDeletion((value) => !value)
        if (!isAwaitingDeletion) {
          props.close('delete')
        }
        return
      }
    } catch (error) {
      if (!(error instanceof Error)) {
        throw error
      }
      openInfoModal(
        {
          text: error.message,
          buttonText: t('modal.info.error.confirm'),
        },
        {
          title: t('modal.info.error.title'),
          name: EModalName.INFO_TABLE_STATE_UPDATE_ERROR,
        },
      )
    }
  }

  useEffect(() => {
    return () => {
      for (const modalName of [
        EModalName.TABLE_ADMIN_SETTINGS,
        EModalName.TABLE_PLAYERS,
        EModalName.CONFIRM_TABLE_DELETE,
      ]) {
        closeModal({
          name: modalName,
        })
      }
    }
  }, [])

  return (
    <div className={drawerContainer}>
      <div className={content}>
        <div className={settingsWrapper}>
          <div className={settingsContainer}>
            <h2 className={h2}>{t('settings.label.gameSettings')}</h2>
            <GameSettings />
          </div>
        </div>
        <div className={settingsWrapper}>
          <div className={settingsContainer}>
            <CardButton
              analytic={{
                name: 'menu',
                params: {
                  menu: 'table_settings',
                  modal: EModalName.TABLE_DRAWER,
                },
              }}
              onClick={onClickTableSettings}
            >
              {t('settings.label.tableSettings')}
            </CardButton>
          </div>
        </div>
        {props.showAdminSettings ? (
          <div className={settingsWrapper}>
            <div className={settingsContainer}>
              <h2 className={h2}>
                {t('modal.tableMenu.adminSettings.header')}
              </h2>
              <CardButton
                analytic={{
                  name: 'menu',
                  params: {
                    menu: 'admin_table_settings',
                    modal: EModalName.TABLE_DRAWER,
                  },
                }}
                onClick={() => {
                  void onClickAdminSettings()
                }}
              >
                {t('modal.tableMenu.adminSettings.tableSettings')}
              </CardButton>
            </div>
          </div>
        ) : null}
        <CardButton
          analytic={{
            name: 'menu',
            params: {
              menu: 'players',
              modal: EModalName.TABLE_DRAWER,
            },
          }}
          onClick={onClickPlayers}
        >
          {t('modal.tableMenu.adminSettings.players')}
        </CardButton>
      </div>

      <div className={basement}>
        {props.showAdminSettings ? (
          <div className={adminButtonsRow}>
            <Button
              className={leaveButton}
              variant='bordered'
              disabled={isUpdatingTableState}
              analytic={{
                name: 'pause',
                params: {
                  modal: EModalName.TABLE_DRAWER,
                },
              }}
              onClick={() => {
                void onClickPauseGame()
              }}
            >
              {isPaused ? (
                <>
                  <span>▶️</span>
                  <span>{t('modal.tableMenu.button.continue')}</span>
                </>
              ) : (
                <>
                  <span>⏸️</span>
                  <span>{t('modal.tableMenu.button.pause')}</span>
                </>
              )}
            </Button>
            <Button
              className={leaveButton}
              variant='bordered'
              disabled={isUpdatingTableState}
              analytic={{
                name: 'delete',
                params: {
                  modal: EModalName.TABLE_DRAWER,
                },
              }}
              onClick={() => {
                void onClickDeleteTable()
              }}
            >
              {isAwaitingDeletion ? (
                <>
                  <span>🔄</span>
                  <span>{t('modal.tableMenu.button.undoDeletion')}</span>
                </>
              ) : (
                <>
                  <span>🗑</span>
                  <span>{t('modal.tableMenu.button.delete')}</span>
                </>
              )}
            </Button>
          </div>
        ) : null}

        <Button
          className={leaveButton}
          variant='primary'
          analytic={{
            name: 'leave',
            params: {
              modal: EModalName.TABLE_DRAWER,
            },
          }}
          onClick={() => props.close('leave')}
        >
          <img
            src={logoutButton}
            alt=''
          />
          <span>{t('modal.tableMenu.button.leave')}</span>
        </Button>
      </div>
    </div>
  )
}
